// =============================================================================
// src/features/crisis/crisis-reputation-bridge.ts
// Ponte Crise -> Reputação: projeção ILUSTRATIVA do índice + atalho de navegação.
//
// Decisão de produto 2026-06-01 (time de análise): liga as duas ilhas que já falam
// do mesmo gabinete acompanhado (Omar Aziz, índice ~49). Cenário de crise ILUSTRATIVO.
// HONESTO por design (revisão do auditor + compliance): NÃO é previsão — é um cenário
// dirigido pela fração de ações do plano aprovadas (estado VIVO de crisis-action-plan).
// Fonte única do K e do baseline; a severidade espelha o gauge "62/100" do markup.
//
// SRP: só a projeção (função pura) + a injeção/atualização do painel-ponte.
// =============================================================================

import { qs, qsa, on } from '../../app/dom';
import { reputationBand, formatReputationNumber } from '../reputation/reputation-data';
import { getLiveReputationIndex } from '../reputation/reputation-live-store';

/** Queda máxima (pts) de uma crise não-respondida — fonte única (pedido do estrategista). */
export const CRISIS_REPUTATION_K = 12;

/** Severidade da crise demonstrada: espelha o gauge estático "62/100" do markup. */
const CRISIS_SEVERITY = 62;

/**
 * Δ ILUSTRATIVO no índice de reputação: proporcional à severidade e totalmente
 * mitigável pela execução no prazo (frac=1 → Δ=0). Crise nunca SOBE reputação, só
 * evita perda; teto de queda em −K. Determinístico (mesma entrada → mesma saída).
 */
export function projectReputationImpact(severity: number, approvedOnTimeFrac: number): number {
  const raw = -CRISIS_REPUTATION_K * (severity / 100) * (1 - approvedOnTimeFrac);
  return Math.max(-CRISIS_REPUTATION_K, Math.min(0, raw));
}

/** Conta ações do plano aprovadas vs. total (estado vivo de crisis-action-plan). */
function countApproved(takeover: HTMLElement): { approved: number; total: number } {
  const total = qsa('#crzPlanList .crz-action', takeover).length;
  const approved = qsa('#crzPlanList .crz-action.is-approved', takeover).length;
  return { approved, total };
}

/** Escreve texto num filho do takeover, se existir. */
function setBridgeText(takeover: HTMLElement, selector: string, text: string): void {
  const el = qs<HTMLElement>(selector, takeover);
  if (el) el.textContent = text;
}

/** Recalcula e pinta a projeção a partir das ações aprovadas no momento. */
function renderBridge(takeover: HTMLElement): void {
  const { approved, total } = countApproved(takeover);
  const frac = total > 0 ? approved / total : 0;
  const delta = projectReputationImpact(CRISIS_SEVERITY, frac);
  // Baseline = índice de reputação VIVO do dono (mesmo valor do header/gauge); nos testes
  // cai no fallback estático (53,15). A projeção é ILUSTRATIVA (severidade fixa 62/100).
  const projected = getLiveReputationIndex() + delta;
  const band = reputationBand(projected);
  const label = approved === 0 ? 'Projeção (sem ação)' : `Projeção (${approved}/${total} ações no prazo)`;
  setBridgeText(takeover, '#crzRepProjLabel', label);
  setBridgeText(takeover, '#crzRepDelta', delta === 0 ? '0,00 · perda evitada' : `${formatReputationNumber(delta)} pts`);
  const valEl = qs<HTMLElement>('#crzRepProjVal', takeover);
  if (!valEl) return;
  valEl.textContent = `${formatReputationNumber(projected)} · ${band.name}`;
  valEl.style.color = band.key === 'good' || band.key === 'exc' ? '#1D9E75' : '#E24B4A';
}

/** Liga o botão "Ver em Reputação": fecha a crise e abre o takeover de reputação. */
function wireOpenReputation(takeover: HTMLElement): void {
  const btn = qs<HTMLElement>('#crzRepOpen', takeover);
  /* v8 ignore next -- markup do takeover sempre tem #crzRepOpen; guarda defensiva CLAUDE.md rule 11 */
  if (!btn) return;
  on(btn, 'click', () => {
    qs<HTMLElement>('#crzClose', takeover)?.click();
    document.getElementById('reputationLauncher')?.click();
  });
}

/**
 * Injeta o painel-ponte no fim da view de crise e o mantém sincronizado com o plano.
 * Registrado APÓS bindCrisisActionPlan: o listener de clique roda depois do dele (mesmo
 * nó), então lê o `.is-approved` já atualizado. No-op sem a view de crise.
 */
export function bindCrisisBridge(takeover: HTMLElement): void {
  const host = qs<HTMLElement>('#crzViewCrisis', takeover);
  if (!host) return;
  host.insertAdjacentHTML('beforeend', bridgePanelHtml());
  renderBridge(takeover);
  const planList = qs<HTMLElement>('#crzPlanList', takeover);
  if (planList) on(planList, 'click', () => renderBridge(takeover));
  wireOpenReputation(takeover);
}

/** Markup do painel-ponte (reusa classes .crz-* existentes; zero CSS novo). */
function bridgePanelHtml(): string {
  return (
    `<section class="crz-panel" id="crzRepBridge" aria-labelledby="crzRepTitle">` +
    `<div class="crz-panel-head">` +
    `<div class="crz-panel-eyebrow"><i class="ti ti-gauge crz-ico sm" aria-hidden="true"></i> Ponte com a Reputação</div>` +
    `<h2 class="crz-panel-title" id="crzRepTitle">Impacto no índice de reputação</h2>` +
    `</div>` +
    `<div class="crz-panel-body">` +
    `<div class="crz-ai-reading"><span class="rk">Como ler</span> A cada ação do plano aprovada no prazo, a projeção de queda diminui; aprovar o plano completo evita a perda, não responder projeta a maior queda.</div>` +
    `<div class="crz-watch-item"><span class="w-name">Índice atual</span><span class="w-val">53,15 · Bom</span></div>` +
    `<div class="crz-watch-item"><span class="w-name" id="crzRepProjLabel">Projeção (sem ação)</span><span class="w-val" id="crzRepProjVal">—</span></div>` +
    `<div class="crz-watch-item"><span class="w-name">Variação</span><span class="w-val" id="crzRepDelta">—</span></div>` +
    `<button class="crz-btn crz-btn-ghost" id="crzRepOpen" type="button"><i class="ti ti-external-link crz-ico sm" aria-hidden="true"></i> Ver em Reputação</button>` +
    `</div></section>`
  );
}
